import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Smile, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Allowed reaction emojis (must match backend)
const REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🔥", "👏", "🎉", "💯", "✅"];
// Popover lays them out in a ~5-wide grid due to max-w-[200px] + p-1.5 each
const GRID_COLS = 5;

/**
 * Emoji grid with arrow-key navigation.
 * Auto-focuses the first emoji when `autoFocus` is true (i.e., opened via keyboard).
 */
const EmojiGrid = ({ emojis, onPick, autoFocus = false, testIdPrefix = 'reaction-emoji' }) => {
  const [idx, setIdx] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (autoFocus && refs.current[0]) {
      refs.current[0].focus();
      setIdx(0);
    }
  }, [autoFocus]);

  const handleKey = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = Math.min(emojis.length - 1, idx + 1);
      setIdx(next);
      refs.current[next]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = Math.max(0, idx - 1);
      setIdx(prev);
      refs.current[prev]?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(emojis.length - 1, idx + GRID_COLS);
      setIdx(next);
      refs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(0, idx - GRID_COLS);
      setIdx(prev);
      refs.current[prev]?.focus();
    }
  };

  return (
    <div
      className="flex gap-1 flex-wrap max-w-[200px]"
      onKeyDown={handleKey}
      data-testid="reaction-picker-content"
    >
      {emojis.map((emoji, i) => (
        <button
          key={emoji}
          ref={(el) => (refs.current[i] = el)}
          onClick={() => onPick(emoji)}
          onFocus={() => setIdx(i)}
          className="p-1.5 hover:bg-secondary focus-visible:bg-secondary focus-visible:ring-2 focus-visible:ring-primary/60 rounded-md transition-colors text-lg outline-none"
          data-testid={`${testIdPrefix}-${emoji}`}
          tabIndex={i === idx ? 0 : -1}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

/**
 * Message Reactions Component
 * Discord/Slack-style emoji reactions
 */

// Reaction Picker Popover
export const ReactionPicker = ({ onSelect, isOpen, onOpenChange, openedByKeyboard = false }) => {
  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-40 group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 transition-opacity"
          aria-label="Add reaction (press R)"
          data-testid="reaction-picker-trigger"
        >
          <Smile className="w-4 h-4 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-2 bg-card border-border z-[60]"
        side="top"
        align="start"
        sideOffset={4}
      >
        <EmojiGrid
          emojis={REACTION_EMOJIS}
          autoFocus={openedByKeyboard}
          onPick={(emoji) => {
            onSelect(emoji);
            onOpenChange(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

// Display reactions on a message
export const MessageReactions = ({ messageId, reactions, currentUserId, onReactionChange }) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleAddReaction = async (emoji) => {
    try {
      const res = await axios.post(`${API}/messages/${messageId}/reactions`, { emoji });
      if (onReactionChange) {
        onReactionChange(res.data.reactions);
      }
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handleRemoveReaction = async (emoji) => {
    try {
      const res = await axios.delete(`${API}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`);
      if (onReactionChange) {
        onReactionChange(res.data.reactions);
      }
    } catch (error) {
      toast.error('Failed to remove reaction');
    }
  };

  const handleToggleReaction = (emoji) => {
    const users = reactions?.[emoji] || [];
    if (users.includes(currentUserId)) {
      handleRemoveReaction(emoji);
    } else {
      handleAddReaction(emoji);
    }
  };

  // Convert reactions object to array for display
  const reactionList = Object.entries(reactions || {}).filter(([, users]) => users.length > 0);

  if (reactionList.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {reactionList.map(([emoji, users]) => {
        const hasReacted = users.includes(currentUserId);
        return (
          <button
            key={emoji}
            onClick={() => handleToggleReaction(emoji)}
            className={`
              inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs
              transition-all duration-150
              ${hasReacted 
                ? 'bg-primary/20 border border-primary/40 text-white' 
                : 'bg-secondary/50 border border-transparent hover:bg-secondary text-slate-300'
              }
            `}
            title={`${users.length} ${users.length === 1 ? 'reaction' : 'reactions'}`}
            data-testid={`reaction-${messageId}-${emoji}`}
          >
            <span>{emoji}</span>
            <span className="text-[10px] font-medium">{users.length}</span>
          </button>
        );
      })}

      {/* Add reaction button */}
      <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
        <PopoverTrigger asChild>
          <button
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-secondary/30 hover:bg-secondary text-slate-400 transition-colors"
            data-testid={`add-reaction-${messageId}`}
          >
            <Plus className="w-3 h-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-2 bg-card border-border" 
          side="top"
          align="start"
        >
          <div className="flex gap-1 flex-wrap max-w-[200px]">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => {
                  handleToggleReaction(emoji);
                  setIsPickerOpen(false);
                }}
                className={`
                  p-1.5 rounded-md transition-colors text-lg
                  ${reactions?.[emoji]?.includes(currentUserId) 
                    ? 'bg-primary/20' 
                    : 'hover:bg-secondary'
                  }
                `}
              >
                {emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Quick reaction bar (appears on hover)
export const QuickReactionBar = ({ messageId, onReact }) => {
  const quickEmojis = ["👍", "❤️", "😂", "🔥"];
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="absolute -top-3 right-0 opacity-0 group-hover:opacity-100 transition-opacity">
      <div className="flex items-center gap-0.5 bg-card border border-border rounded-full px-1 py-0.5 shadow-lg">
        {quickEmojis.map((emoji) => (
          <button
            key={emoji}
            onClick={() => onReact(emoji)}
            className="p-1 hover:bg-secondary rounded-full transition-colors text-sm"
            data-testid={`quick-react-${messageId}-${emoji}`}
          >
            {emoji}
          </button>
        ))}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <button className="p-1 hover:bg-secondary rounded-full transition-colors">
              <Plus className="w-3 h-3 text-slate-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-auto p-2 bg-card border-border" 
            side="top"
          >
            <div className="flex gap-1 flex-wrap max-w-[180px]">
              {REACTION_EMOJIS.filter(e => !quickEmojis.includes(e)).map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onReact(emoji);
                    setIsOpen(false);
                  }}
                  className="p-1.5 hover:bg-secondary rounded-md transition-colors text-lg"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default MessageReactions;
